import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  ClockIcon as ClockIconSolid,
  MinusCircleIcon,
  PlusCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import Tooltip from '@onbeef/components/tooltip';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import React from 'react';
import { useGetCustomerPointsHistory } from '../../../queries/loyalty';
import {
  LoyaltyPointHistoryEventType,
  LoyaltyPointHistoryStatus,
  CustomerPointsHistoryResponse,
} from '../../../queries/loyalty/types';
import { useMerchantStore } from '../../../stores/merchant';

export const LoyaltyPointsHistory = () => {
  const merchantLoyaltyProgramme = useMerchantStore(
    (state) => state.loyalty_programme,
  );

  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);

  const { data: pointsHistory, isLoading: isLoadingPointsHistory } =
    useGetCustomerPointsHistory(page, merchantLoyaltyProgramme?.id);

  React.useEffect(() => {
    if (pointsHistory?.last_page) {
      setTotalPages(pointsHistory.last_page);
    }
  }, [pointsHistory]);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <div className="p-5">
      <h3 className="text-lg font-semibold text-gray-800 mb-3">Histórico</h3>

      <PointsHistoryUI
        pointsHistory={pointsHistory}
        isLoadingPointsHistory={isLoadingPointsHistory}
        page={page}
        totalPages={totalPages}
        handleChangePage={handleChangePage}
      />
    </div>
  );
};

interface PointsHistoryProps {
  pointsHistory?: CustomerPointsHistoryResponse | null;
  isLoadingPointsHistory: boolean;
  page: number;
  totalPages: number;
  handleChangePage: (newPage: number) => void;
}

export const PointsHistoryUI = ({
  pointsHistory,
  isLoadingPointsHistory,
  page,
  totalPages,
  handleChangePage,
}: PointsHistoryProps) => {
  const formatDate = (date: string) => {
    try {
      const dateFormatted = new Date(date);
      return format(dateFormatted, "d 'de' MMMM, yyyy", { locale: ptBR });
    } catch (error) {
      return date;
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-4">
        {isLoadingPointsHistory ? (
          <PointsHistorySkeleton />
        ) : (
          pointsHistory?.data?.map((history) => {
            let rewardName =
              history.event_type === LoyaltyPointHistoryEventType.SPENT
                ? history.order?.loyalty_programme_reward_discount?.POINTS
                    ?.rewards?.[0]?.name
                : '';

            return (
              <div
                key={history.id}
                className="border border-gray-100 rounded-lg p-3 flex flex-col gap-2"
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <EventTypeBadge eventType={history.event_type} />
                  </div>
                  <StatusBadge status={history.status} />
                </div>

                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-gray-700 flex-shrink-0">
                    {history.event_type === LoyaltyPointHistoryEventType.EARNED
                      ? '+'
                      : '-'}
                    {history.amount_points} pontos
                  </span>

                  {rewardName && (
                    <div className="flex items-center gap-2 text-right line-clamp-3">
                      <Tooltip text={rewardName}>
                        <span className="text-sm text-gray-500">
                          {rewardName}
                        </span>
                      </Tooltip>
                    </div>
                  )}
                </div>

                <div className="mt-1 flex justify-between items-center text-xs text-gray-500">
                  <div>
                    {history.order ? (
                      <p>Pedido #{history.order?.code}</p>
                    ) : (
                      <p>Importado de outro sistema</p>
                    )}
                  </div>
                  <div>
                    <p>{formatDate(history.created_at)}</p>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="mt-6 flex justify-center">
        {isLoadingPointsHistory ? (
          <div className="py-2">Carregando...</div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                handleChangePage(page - 1);
              }}
              disabled={page <= 1}
              className={`inline-flex items-center p-2 rounded-md ${
                page <= 1
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              aria-label="Página anterior"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>

            <div className="text-sm text-gray-700">
              Página {pointsHistory?.current_page || page} de {totalPages || 1}
            </div>

            <button
              onClick={(e) => {
                e.preventDefault();
                handleChangePage(page + 1);
              }}
              disabled={page >= totalPages}
              className={`inline-flex items-center p-2 rounded-md ${
                page >= totalPages
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              aria-label="Próxima página"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const StatusBadge = ({ status }: { status: LoyaltyPointHistoryStatus }) => {
  const statusConfig = {
    [LoyaltyPointHistoryStatus.PENDING]: {
      color: 'bg-yellow-100 text-yellow-800',
      icon: <ClockIconSolid className="w-4 h-4" />,
      label: 'Pendente',
    },
    [LoyaltyPointHistoryStatus.CANCELED]: {
      color: 'bg-red-100 text-red-800',
      icon: <XCircleIcon className="w-4 h-4" />,
      label: 'Cancelado',
    },
    [LoyaltyPointHistoryStatus.EXPIRED]: {
      color: 'bg-gray-100 text-gray-800',
      icon: <ClockIcon className="w-4 h-4" />,
      label: 'Expirado',
    },
    default: {
      color: 'bg-gray-100 text-gray-800',
      icon: null,
      label: status,
    },
  };

  if (status === LoyaltyPointHistoryStatus.VALID) return null;

  const config = statusConfig[status] || statusConfig.default;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
    >
      {config.icon}
      {config.label}
    </span>
  );
};

const EventTypeBadge = ({
  eventType,
}: {
  eventType: LoyaltyPointHistoryEventType;
}) => {
  const eventConfig = {
    [LoyaltyPointHistoryEventType.EARNED]: {
      color: 'bg-green-100 text-green-800',
      icon: <PlusCircleIcon className="w-4 h-4" />,
      label: 'Ganhou',
    },
    [LoyaltyPointHistoryEventType.SPENT]: {
      color: 'bg-red-100 text-red-800',
      icon: <MinusCircleIcon className="w-4 h-4" />,
      label: 'Resgatou',
    },
    default: {
      color: 'bg-gray-100 text-gray-800',
      icon: null,
      label: eventType,
    },
  };

  const config = eventConfig![eventType] || eventConfig.default;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
    >
      {config.icon}
      {config.label}
    </span>
  );
};

const PointsHistorySkeleton = () => {
  return (
    <>
      {Array.from({ length: 5 }).map((_, index) => (
        <div
          key={`skeleton-${index}`}
          className="border border-gray-100 rounded-lg p-3 flex flex-col gap-2"
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="bg-gray-200 animate-pulse h-6 w-20 rounded-full"></div>
            </div>
            <div className="bg-gray-200 animate-pulse h-6 w-24 rounded-full"></div>
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="bg-gray-200 animate-pulse h-6 w-32 rounded-md"></div>
            <div className="bg-gray-200 animate-pulse h-6 w-24 rounded-md"></div>
          </div>

          <div className="mt-1 flex justify-between items-center">
            <div>
              <div className="bg-gray-200 animate-pulse h-4 w-28 rounded-md"></div>
            </div>
            <div>
              <div className="bg-gray-200 animate-pulse h-4 w-36 rounded-md"></div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
};
